// Usage Banner - Shows free tier usage info and referral link
import { useState, useEffect } from 'react';
import { Users, Gift, Copy, Check, AlertTriangle, ArrowRight } from 'lucide-react';
import { getUserUsage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function UsageBanner({ onLimitReached }) {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchUsage = async () => {
      if (!user) return;

      try {
        const result = await getUserUsage();
        setUsage(result.usage);

        // Notify parent if limit reached
        if (!result.usage.hasPlan && result.usage.remaining <= 0 && onLimitReached) {
          onLimitReached(result.usage);
        }
      } catch (error) {
        console.error('Failed to fetch usage:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsage();
  }, [user, onLimitReached]);

  const copyReferralLink = async () => {
    if (!usage?.referralLink) return;

    try {
      await navigator.clipboard.writeText(usage.referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (loading || !usage) return null;

  // Don't show for users with a paid plan
  if (usage.hasPlan) return null;

  const isLimitReached = usage.remaining <= 0;
  const isLow = usage.remaining <= 2 && usage.remaining > 0;

  return (
    <div className={`rounded-lg border ${
      isLimitReached
        ? 'bg-red-50 border-red-200'
        : isLow
          ? 'bg-amber-50 border-amber-200'
          : 'bg-blue-50 border-blue-200'
    }`}>
      {/* Compact View */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          {isLimitReached ? (
            <AlertTriangle className="w-5 h-5 text-red-600" />
          ) : (
            <Gift className="w-5 h-5 text-blue-600" />
          )}
          <div>
            <span className={`font-medium ${isLimitReached ? 'text-red-900' : 'text-gray-900'}`}>
              {isLimitReached
                ? 'Free calls used up'
                : `${usage.remaining} free call${usage.remaining !== 1 ? 's' : ''} remaining`
              }
            </span>
            <span className="text-sm text-gray-500 ml-2">
              ({usage.callsUsed}/{usage.totalAvailable} used)
            </span>
          </div>
        </div>
        <ArrowRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {/* Expanded View */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-200/50">
          {/* Usage breakdown */}
          <div className="mt-3 mb-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-600">Usage</span>
              <span className="text-gray-900 font-medium">{usage.callsUsed} / {usage.totalAvailable}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  isLimitReached ? 'bg-red-500' : isLow ? 'bg-amber-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min((usage.callsUsed / usage.totalAvailable) * 100, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{usage.baseCalls} base + {usage.referralBonusCalls} bonus</span>
              {usage.successfulReferrals > 0 && (
                <span className="text-green-600">{usage.successfulReferrals} referral{usage.successfulReferrals !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>

          {/* Referral section */}
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-purple-600" />
              <span className="font-medium text-gray-900">Earn {usage.bonusPerReferral} bonus calls per invite</span>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Share your referral link. When friends sign up, you both win!
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={usage.referralLink}
                className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-600 truncate"
              />
              <button
                onClick={copyReferralLink}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
          </div>

          {/* Upgrade CTA */}
          {isLimitReached && (
            <a
              href="/pricing"
              className="mt-3 block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Upgrade for unlimited calls
            </a>
          )}
        </div>
      )}
    </div>
  );
}
